import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import Modal from './Modal';
import {WordList} from '../../domain/types';
import {WorkspaceRow} from '../../store/selectors/workspace.selectors';
import {AlertCircleIcon, AlertTriangleIcon, WandIcon} from '../Icons';
import WordListMultiSelect from './WordListMultiSelect';
import {VIDEO_EDITOR_LABELS, VideoEditor} from '../../backend-types/user';

/** UI wording → the API's marker mode. The mapping is not one-to-one, so it lives in one place. */
export type MarkerOption = 'none' | 'markers' | 'markers-with-word';

const MARKER_LABELS: {value: MarkerOption; label: string}[] = [
  {value: 'markers', label: 'Add markers (shows a word was censored)'},
  {value: 'markers-with-word', label: 'Add markers, including the censored word'},
  {value: 'none', label: 'No markers'},
];

/** The frame rates people actually cut at. `fps` still stores a plain string, decimals included. */
const FPS_PRESETS: {value: string; label: string}[] = [
  {value: '23.976', label: '23.976 fps (Film/NTSC)'},
  {value: '24', label: '24 fps (Film)'},
  {value: '25', label: '25 fps (PAL)'},
  {value: '29.97', label: '29.97 fps (NTSC) — default'},
  {value: '30', label: '30 fps'},
  {value: '50', label: '50 fps (PAL)'},
  {value: '59.94', label: '59.94 fps (NTSC)'},
  {value: '60', label: '60 fps'},
];
// Sentinel for the "Custom…" option — distinct from any real fps string so it can't collide with
// a preset value (or a custom one someone happens to type that matches a preset, which just
// reselects that preset instead).
const CUSTOM_FPS_OPTION = '__custom__';

// Windows: `C:\...`, `C:/...`, or a UNC path like `\\server\share\file.mp3`.
const WINDOWS_PATH_RE = /^[a-zA-Z]:[\\/]|^\\\\[^\\/]+[\\/][^\\/]+/;
// Mac: an absolute path with more than one segment, e.g. `/Users/name/episode.mp3`.
// (This also happens to match Linux paths, which is fine — we're not trying to exclude them.)
const MAC_PATH_RE = /^\/[^/]+\/.+/;

/**
 * Heuristic only — just enough to tell a real file path from a bare filename like
 * `episode-42.mp3`. A false negative here just means an unnecessary warning, not a bad XML.
 */
function looksLikeFilePath(value: string): boolean {
  return WINDOWS_PATH_RE.test(value) || MAC_PATH_RE.test(value);
}

export interface ConversionSubmission {
  /**
   * The word lists to combine, in the order picked — at most five. The server merges and
   * de-duplicates them, so overlap between two lists is not this dialog's problem.
   *
   * Non-empty by the time it gets here: an empty array would not mean "censor nothing" to the API,
   * it would fall back to the project's default list, which is a different conversion from the one
   * an empty picker looks like it is asking for.
   */
  wordListIds: string[];
  outputFileName: string;
  fps: number;
  markerOption: MarkerOption;
  /**
   * Written into the XML as the media reference. Always non-empty by the time it gets here — the
   * modal falls back to the original filename when the field is left blank — because a clip that
   * names no file at all cannot be relinked in any editor. A name that doesn't resolve is
   * recoverable; an empty one is not.
   */
  audioPath: string;
}

interface NewConversionModalProps {
  row: WorkspaceRow | null;
  wordLists: WordList[];
  /** The project's default word list, pre-selected so the common case is still one click. */
  defaultWordListId?: string;
  /**
   * The verbatim `message` from a rejected convert, when the reason was the word lists themselves
   * (`wordlist_not_found`, `too_many_wordlists`, `invalid_wordlist_ids`). Shown as written rather
   * than paraphrased, next to the picker that has to change for the retry to work.
   */
  wordListError: string | null;
  /** Ids from a `wordlist_not_found` — marked in the picker so the user knows which to remove. */
  missingWordListIds: string[];
  isConverting: boolean;
  /** Set when the source audio has no sample rate or duration — the converter needs both. */
  blockedReason: string | null;
  /** `null` means "never asked" — read alongside `prefersFullMediaPath`, not compared directly. */
  videoEditor: VideoEditor | null;
  /**
   * From `prefersFullMediaPathSelector` (`store/selectors/auth.selectors.ts`). True for DaVinci
   * Resolve and for `videoEditor === null` alike — an editor we've never characterised is described
   * in the more cautious terms. It picks the wording of the note below the field and nothing else:
   * a bare filename never blocks a conversion.
   */
  prefersFullMediaPath: boolean;
  onCancel: () => void;
  onConfirm: (submission: ConversionSubmission) => void;
}

/**
 * Conversion is the cheap half of the product and the dialog says so.
 *
 * Nothing here is metered — the same transcription can be converted against a different word list
 * as many times as the user likes — so this asks for the four things that actually vary and gets
 * out of the way. The heavy `Formik`/`Yup` form the desktop app used is gone: four fields, three of
 * which have a working default, do not need a validation library.
 *
 * The output filename is required with no fallback. The audio path is different: it's pre-filled
 * with whatever path we know (falling back to the plain filename) and never blocks Convert — a
 * filename that doesn't resolve is something every editor can be pointed at after the import, and
 * CensorMe Mapper can rewrite the paths in a finished XML without converting again. All
 * `prefersFullMediaPath` changes is which note explains that: Premiere and Final Cut Pro flag the
 * clip offline themselves, while DaVinci Resolve (and an editor we've never asked about) wants the
 * file added to the project before it links up. See `ConversionSubmission.audioPath` and
 * `looksLikeFilePath`.
 */
const NewConversionModal: React.FC<NewConversionModalProps> = ({
  row,
  wordLists,
  defaultWordListId,
  wordListError,
  missingWordListIds,
  isConverting,
  blockedReason,
  videoEditor,
  prefersFullMediaPath,
  onCancel,
  onConfirm,
}) => {
  const [wordListIds, setWordListIds] = useState<string[]>([]);
  const [outputFileName, setOutputFileName] = useState('');
  const [fps, setFps] = useState('29.97');
  // Whether the fps field is showing the free-text box instead of the preset dropdown. Only true
  // once the user has explicitly picked "Custom…" — a preset covers every default case.
  const [customFpsMode, setCustomFpsMode] = useState(false);
  const [markerOption, setMarkerOption] = useState<MarkerOption>('markers');
  const [audioPath, setAudioPath] = useState('');
  // Convert is never disabled for a fixable validation problem — clicking it while one exists is
  // what turns this on, which is what puts the word-list error (and any other unmet requirement)
  // in front of the user instead of leaving them to guess why nothing happened.
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  useEffect(() => {
    if (!row) {
      return;
    }
    setWordListIds(defaultWordListId ? [defaultWordListId] : []);
    setOutputFileName(row.activeTranscription?.name ?? row.audio.name.replace(/\.[^/.]+$/, ''));
    setFps('29.97');
    setCustomFpsMode(false);
    setMarkerOption('markers');
    // Falls back to the uploaded filename rather than blank: the name we already know is a better
    // starting point than an empty box the user has to fill in by hand. It's still just a filename,
    // not a path, so `audioPathLooksReal` below will (correctly) note that it won't link on its own.
    // Re-set on every row, not just the first: left alone it would carry the previous file's path
    // into this one's XML, which is a wrong media reference rather than a missing one.
    setAudioPath(row.audio.localAudioPath?.trim() || row.audio.name);
    // A fresh row is a fresh form — don't carry over "you tried to convert without X" styling from
    // whatever the user was looking at before.
    setAttemptedSubmit(false);
  }, [row, defaultWordListId]);

  if (!row) {
    return null;
  }

  const isWordListValid = wordListIds.length > 0;
  const parsedFps = Number.parseFloat(fps);
  const isFpsValid = Number.isFinite(parsedFps) && parsedFps > 0;
  const trimmedOutputFileName = outputFileName.trim();
  const isOutputFileNameValid = trimmedOutputFileName.length > 0;
  // The field itself is optional: left blank, the original filename is used, same as the field's
  // own initial value. Either way what actually goes in the XML is `effectiveAudioPath`.
  const trimmedAudioPath = audioPath.trim();
  const effectiveAudioPath = trimmedAudioPath || row.audio.name;
  const audioPathLooksReal = looksLikeFilePath(effectiveAudioPath);
  // A bare filename is a note, never a blocker — whichever editor they're on, the clip can be
  // pointed at the file after the import, or the XML repaired later with CensorMe Mapper.
  const canConvert = isWordListValid
    && isFpsValid
    && isOutputFileNameValid
    && !isConverting
    && !blockedReason;
  // Only a hard, unfixable-from-this-form state disables the button outright. Everything else
  // (missing word list, bad fps, blank filename) is left clickable — clicking surfaces the errors
  // on the fields responsible instead of silently doing nothing.
  const isButtonDisabled = isConverting || Boolean(blockedReason);
  const showWordListError = attemptedSubmit && !isWordListValid;
  const editorLabel = videoEditor ? VIDEO_EDITOR_LABELS[videoEditor] : null;

  return (
    <Modal
      isOpen
      title="New conversion"
      subtitle={
        <>
          <span className="text-gray-400">Original file: </span>
          <span className="text-gray-200">{row.audio.name}</span>
          <span className="mx-2 text-gray-700">·</span>
          <span className="text-gray-400">Transcription: </span>
          <span className="text-emerald-300">Ready</span>
        </>
      }
      onClose={onCancel}
      footer={
        <>
          {attemptedSubmit && !canConvert && !isButtonDisabled && (
            <p className="mr-auto text-xs text-red-400">Fix the highlighted fields above.</p>
          )}
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-1.5 text-sm text-gray-400 hover:text-gray-100
             hover:bg-gray-800 transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isButtonDisabled}
            onClick={() => {
              if (!canConvert) {
                setAttemptedSubmit(true);
                return;
              }
              onConfirm({
                wordListIds,
                outputFileName: trimmedOutputFileName,
                fps: parsedFps,
                markerOption,
                audioPath: effectiveAudioPath,
              });
            }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm
             font-medium text-white hover:bg-indigo-500 transition-colors
             disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          >
            <WandIcon className="w-4 h-4" />
            {isConverting ? 'Converting…' : 'Convert to XML'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {blockedReason && (
          <div className="flex gap-2 rounded-lg border border-red-500/30 bg-red-500/[0.06] px-3 py-2.5
           text-sm text-red-200">
            <AlertTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
            <p>{blockedReason}</p>
          </div>
        )}

        <div>
          <p className="text-xs text-gray-500 pb-2">
            Conversions are unlimited and free. You can run as many as you want from the same
            transcription, against a different set of lists each time.
          </p>

          <WordListMultiSelect
            lists={wordLists}
            selectedIds={wordListIds}
            onChange={setWordListIds}
            missingIds={missingWordListIds}
            invalid={showWordListError || Boolean(wordListError)}
            disabled={isConverting}
          />

          {showWordListError && (
            <p className="mt-1 text-xs text-red-400">
              Choose at least one word list before converting.
            </p>
          )}

          {/* The server's own sentence, shown as written: it names the ids that could not be read
              and what to do about them, and rewording it here would only lose that. Deliberately
              not a toast — the fix is in the picker directly above it. */}
          {wordListError && (
            <div className="mt-2 flex gap-2 rounded-lg border border-red-500/30 bg-red-500/[0.06]
             px-3 py-2.5 text-xs text-red-200">
              <AlertTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
              <p>{wordListError}</p>
            </div>
          )}

          {wordListIds.length > 1 && (
            <p className="mt-1.5 text-[11px] text-gray-500">
              These are merged into one list before matching, so a word on more than one of them
              is still cut once.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="conversion-output" className="mb-1 block text-xs font-medium text-gray-300">
              Output filename
            </label>
            <input
              id="conversion-output"
              type="text"
              value={outputFileName}
              onChange={event => setOutputFileName(event.target.value)}
              placeholder={row.audio.name.replace(/\.[^/.]+$/, '')}
              aria-invalid={!isOutputFileNameValid}
              className={`${controlClass} ${isOutputFileNameValid ? '' : 'border-red-500/50'}`}
            />
            {!isOutputFileNameValid && (
              <p className="mt-1 text-xs text-red-400">Give the XML a name.</p>
            )}
          </div>
          <div>
            <label htmlFor="conversion-fps" className="mb-1 block text-xs font-medium text-gray-300">
              Frames per second
            </label>
            {customFpsMode ? (
              <div className="flex gap-2">
                <input
                  id="conversion-fps"
                  type="text"
                  inputMode="decimal"
                  autoFocus
                  value={fps}
                  onChange={event => setFps(event.target.value)}
                  placeholder="e.g. 47.95"
                  className={`${controlClass} ${isFpsValid ? '' : 'border-red-500/50'}`}
                />
                <button
                  type="button"
                  onClick={() => {
                    setCustomFpsMode(false);
                    setFps('29.97');
                  }}
                  className="shrink-0 rounded-lg border border-gray-800 px-2.5 text-xs text-gray-400
                   hover:text-gray-100 hover:bg-gray-800 transition-colors cursor-pointer"
                >
                  Presets
                </button>
              </div>
            ) : (
              <select
                id="conversion-fps"
                value={fps}
                onChange={(event) => {
                  if (event.target.value === CUSTOM_FPS_OPTION) {
                    setCustomFpsMode(true);
                    setFps('');
                  } else {
                    setFps(event.target.value);
                  }
                }}
                className={`${controlClass} ${isFpsValid ? '' : 'border-red-500/50'}`}
              >
                {FPS_PRESETS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
                <option value={CUSTOM_FPS_OPTION}>Custom…</option>
              </select>
            )}
            {!isFpsValid && <p className="mt-1 text-xs text-red-400">Must be a number above 0.</p>}
          </div>
        </div>

        <div>
          <label htmlFor="conversion-markers" className="mb-1 block text-xs font-medium text-gray-300">
            Markers
          </label>
          <select
            id="conversion-markers"
            value={markerOption}
            onChange={event => setMarkerOption(event.target.value as MarkerOption)}
            className={controlClass}
          >
            {MARKER_LABELS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="conversion-audio-path" className="mb-1 block text-xs font-medium text-gray-300">
            Audio file name or path on your machine
          </label>
          <input
            id="conversion-audio-path"
            type="text"
            value={audioPath}
            onChange={event => setAudioPath(event.target.value)}
            placeholder={`Leave blank to use "${row.audio.name}"`}
            className={controlClass}
          />
          {audioPathLooksReal ? (
            <p className="mt-1 text-xs text-gray-600">
              Written into the XML as the media reference. The full path lets your editor relink the
              audio on its own; a bare filename means pointing it at the file yourself after the
              import.
            </p>
          ) : prefersFullMediaPath ? (
            // Amber and advisory, exactly like the Premiere/FCP note below — the only difference is
            // what relinking looks like in Resolve, which doesn't mark the clip offline for you.
            // Nothing here blocks Convert: a bare filename is recoverable in every editor, and the
            // Mapper repairs a finished XML without converting again.
            <div className="mt-2 flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.06]
             px-3 py-2.5 text-xs text-amber-200">
              <AlertCircleIcon className="w-4 h-4 shrink-0 mt-0.5" />
              <p>
                {editorLabel
                  ? `${editorLabel} won't mark this clip offline if the path doesn't resolve — `
                    + 'add the audio file to your project after importing and it links up, as long '
                    + 'as the filename still matches.'
                  : 'We don\u2019t know which editor you cut in yet. If the path doesn\u2019t '
                    + 'resolve, you may have to add the audio file to your project after importing '
                    + 'to link the clip up \u2014 which works as long as the filename still matches.'}
                {' '}Enter the full path here to skip that step. Otherwise, you may need to repair it
                later using the{' '}
                <Link to="/downloads" target="_blank" className="underline text-amber-100 hover:text-amber-50">CensorMe Mapper</Link>.
                {!editorLabel && (
                  <>
                    {' '}Set your editor in <Link
                      to="/account"
                      className="underline hover:text-amber-100"
                    >
                      Settings
                    </Link> for advice that matches it.
                  </>
                )}
              </p>
            </div>
          ) : (
            <div className="mt-2 flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.06]
             px-3 py-2.5 text-xs text-amber-200">
              <AlertCircleIcon className="w-4 h-4 shrink-0 mt-0.5" />
              <p>
                {editorLabel} will show this clip as offline media if the path doesn&rsquo;t
                resolve, and finish the import — you can relink it there whenever you like. Enter
                the full path here to skip that step. Otherwise, you may need to repair it later using the {' '}
                <Link to="/downloads" target="_blank" className="underline text-amber-100 hover:text-amber-50">CensorMe Mapper</Link>.
              </p>
            </div>
          )}
        </div>

      </div>
    </Modal>
  );
};

const controlClass = 'w-full rounded-lg border border-gray-800 bg-gray-950/60 px-3 py-2 text-sm '
  + 'text-gray-100 placeholder-gray-600 focus:border-indigo-500 focus:outline-none '
  + 'focus:ring-1 focus:ring-indigo-500';

export default NewConversionModal;
